import {useState} from 'react';

import {Box, ButtonBase, Stack, Typography} from '@mui/material';

import {CHECK_STATUS} from './seoConstants';
import SeoScoreBadge from './SeoScoreBadge';

const STATUS_ICON = {
    [CHECK_STATUS.PASS]: {symbol: '\u2713', color: '#1D9E75'},
    [CHECK_STATUS.WARN]: {symbol: '!', color: '#BA7517'},
    [CHECK_STATUS.FAIL]: {symbol: '\u2717', color: '#D85A30'},
    [CHECK_STATUS.INFO]: {symbol: 'i', color: '#8d877f'},
    [CHECK_STATUS.BONUS]: {symbol: '\u2605', color: '#1D9E75'},
};

function CheckItem({check: c}) {
    const icon = STATUS_ICON[c.status] || STATUS_ICON.info;
    return (
        <Stack direction="row" alignItems="flex-start" gap="8px" sx={{padding: '4px 0'}}>
            <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: `${icon.color}18`,
                color: icon.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
                marginTop: '1px',
            }}>
                {icon.symbol}
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: 11.5, fontWeight: 600, color: 'var(--cg-text)', lineHeight: 1.3}}>
                    {c.label}
                </Typography>
                <Typography sx={{fontSize: 11, color: 'var(--cg-text-muted)', lineHeight: 1.4}}>
                    {c.message}
                </Typography>
            </Box>
            {c.value && (
                <Typography sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: icon.color,
                    fontFamily: 'monospace',
                    flexShrink: 0,
                    marginTop: '2px',
                }}>
                    {c.value}
                </Typography>
            )}
        </Stack>
    );
}

// ── Per-field SEO panel (collapsible) ────────────────────────────────

export function FieldSeoPanel({analysis, onFix = null, fixing = false}) {
    if (!analysis?.checks?.length) return null;

    // Only WARN and FAIL are actionable — INFO and BONUS are either neutral
    // or positive, and PASS is already resolved.
    const fixableCount = analysis.checks.filter(
        (c) => c.status === CHECK_STATUS.WARN || c.status === CHECK_STATUS.FAIL,
    ).length;
    const showFixButton = Boolean(onFix) && fixableCount > 0;

    return (
        <Box sx={{
            padding: '8px 12px',
            borderTop: '1px solid var(--cg-border)',
            background: 'var(--cg-surface-alt)',
        }}>
            {showFixButton && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{paddingBottom: '6px', marginBottom: '4px', borderBottom: '1px dashed var(--cg-border)'}}>
                    <Typography sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: 'var(--cg-text-soft)',
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                    }}>
                        {fixableCount} issue{fixableCount === 1 ? '' : 's'} to fix
                    </Typography>
                    <ButtonBase
                        onClick={onFix}
                        disabled={fixing}
                        sx={{
                            border: '1px solid #1D9E7555',
                            background: fixing ? '#1D9E7514' : '#1D9E7508',
                            color: '#1D9E75',
                            borderRadius: 999,
                            padding: '3px 10px',
                            fontSize: 11,
                            fontWeight: 700,
                            fontFamily: 'inherit',
                            cursor: fixing ? 'not-allowed' : 'pointer',
                            opacity: fixing ? 0.7 : 1,
                            '&:hover': {background: fixing ? '#1D9E7514' : '#1D9E7518'},
                        }}>
                        {fixing ? 'Fixing…' : 'Fix with AI'}
                    </ButtonBase>
                </Stack>
            )}
            {analysis.checks.map((c) => (
                <CheckItem key={c.id} check={c} />
            ))}
        </Box>
    );
}

// ── Listing-level SEO overview (collapsible) ─────────────────────────

export function ListingSeoPanel({analysis}) {
    const [expanded, setExpanded] = useState(false);

    if (!analysis) return null;

    return (
        <Box sx={{
            border: '1px solid var(--cg-border)',
            borderRadius: '12px',
            background: 'var(--cg-surface)',
            overflow: 'hidden',
        }}>
            <ButtonBase
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                }}>
                <Stack direction="row" alignItems="center" gap="10px">
                    <Typography sx={{fontSize: 12, fontWeight: 700, color: 'var(--cg-text)', textTransform: 'uppercase', letterSpacing: '.04em'}}>
                        SEO Score
                    </Typography>
                    <SeoScoreBadge grade={analysis.grade} score={analysis.score} />
                </Stack>
                <Typography sx={{fontSize: 12, color: 'var(--cg-text-muted)'}}>
                    {expanded ? 'Collapse' : 'Details'}
                </Typography>
            </ButtonBase>

            {expanded && (
                <Box sx={{borderTop: '1px solid var(--cg-border)', padding: '12px 14px'}}>
                    {/* Keyword coverage */}
                    {analysis.keywordCoverage?.length > 0 && (
                        <Box sx={{marginBottom: '12px'}}>
                            <Typography sx={{fontSize: 11, fontWeight: 700, color: 'var(--cg-text-soft)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '6px'}}>
                                Keyword coverage
                            </Typography>
                            <Stack gap="3px">
                                {analysis.keywordCoverage.map((kw) => (
                                    <Stack key={kw.term} direction="row" alignItems="center" gap="8px" sx={{padding: '3px 0'}}>
                                        <Box sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: kw.present ? '#1D9E75' : '#D85A30',
                                            flexShrink: 0,
                                        }} />
                                        <Typography sx={{
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: 'var(--cg-text)',
                                            fontFamily: 'monospace',
                                        }}>
                                            "{kw.term}"
                                        </Typography>
                                        <Typography sx={{fontSize: 11, color: 'var(--cg-text-muted)'}}>
                                            {kw.present ? `${kw.count}x used` : 'not found'}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Listing checks */}
                    <Typography sx={{fontSize: 11, fontWeight: 700, color: 'var(--cg-text-soft)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '6px'}}>
                        Listing checks
                    </Typography>
                    {analysis.listingChecks.map((c) => (
                        <CheckItem key={c.id} check={c} />
                    ))}
                </Box>
            )}
        </Box>
    );
}
